import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Eye, EyeOff } from "lucide-react";

function AnimatedDiagramRegister() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <svg viewBox="0 0 440 380" className="w-full max-w-[380px] mx-auto" fill="none">
        {/* Connections */}
        <path d="M220 60 L120 150" stroke="white" strokeWidth="1.5" strokeOpacity="0.2" strokeDasharray="5 4">
          <animate attributeName="stroke-dashoffset" values="18;0" dur="2s" repeatCount="indefinite" />
        </path>
        <path d="M220 60 L320 150" stroke="white" strokeWidth="1.5" strokeOpacity="0.2" strokeDasharray="5 4">
          <animate attributeName="stroke-dashoffset" values="18;0" dur="2.3s" repeatCount="indefinite" />
        </path>
        <path d="M120 190 L220 260" stroke="white" strokeWidth="1.5" strokeOpacity="0.15" strokeDasharray="5 4">
          <animate attributeName="stroke-dashoffset" values="18;0" dur="1.7s" repeatCount="indefinite" />
        </path>
        <path d="M320 190 L220 260" stroke="white" strokeWidth="1.5" strokeOpacity="0.15" strokeDasharray="5 4">
          <animate attributeName="stroke-dashoffset" values="18;0" dur="2.1s" repeatCount="indefinite" />
        </path>

        {/* Flow dots */}
        <circle r="2.5" fill="white" opacity="0.5">
          <animateMotion dur="2s" repeatCount="indefinite" path="M220 60 L120 150" />
        </circle>
        <circle r="2.5" fill="white" opacity="0.5">
          <animateMotion dur="2.3s" repeatCount="indefinite" path="M220 60 L320 150" />
        </circle>

        <defs>
          <filter id="glow2"><feGaussianBlur stdDeviation="2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>

        {/* Top card */}
        <g filter="url(#glow2)">
          <rect x="160" y="30" width="120" height="46" rx="10" fill="white" fillOpacity="0.12" stroke="white" strokeWidth="1" strokeOpacity="0.25">
            <animate attributeName="fill-opacity" values="0.12;0.18;0.12" dur="3s" repeatCount="indefinite" />
          </rect>
          <circle cx="177" cy="48" r="4" fill="#818CF8" />
          <text x="187" y="52" fill="white" fontSize="9" fontWeight="700" fontFamily="system-ui">Équipe Projet</text>
          <text x="177" y="66" fill="white" fillOpacity="0.4" fontSize="6.5" fontFamily="system-ui">EQUIPE</text>
        </g>

        {/* Left card */}
        <g>
          <rect x="60" y="142" width="120" height="46" rx="10" fill="white" fillOpacity="0.09" stroke="white" strokeWidth="0.8" strokeOpacity="0.18">
            <animate attributeName="fill-opacity" values="0.09;0.15;0.09" dur="3.5s" repeatCount="indefinite" />
          </rect>
          <circle cx="77" cy="160" r="4" fill="#F59E0B" />
          <text x="87" y="164" fill="white" fontSize="9" fontWeight="600" fontFamily="system-ui">Designer</text>
          <text x="77" y="178" fill="white" fillOpacity="0.3" fontSize="6.5" fontFamily="system-ui">UI/UX</text>
        </g>

        {/* Right card */}
        <g>
          <rect x="260" y="142" width="120" height="46" rx="10" fill="white" fillOpacity="0.09" stroke="white" strokeWidth="0.8" strokeOpacity="0.18">
            <animate attributeName="fill-opacity" values="0.09;0.14;0.09" dur="4s" repeatCount="indefinite" />
          </rect>
          <circle cx="277" cy="160" r="4" fill="#34D399" />
          <text x="287" y="164" fill="white" fontSize="9" fontWeight="600" fontFamily="system-ui">Dev Backend</text>
          <text x="277" y="178" fill="white" fillOpacity="0.3" fontSize="6.5" fontFamily="system-ui">API</text>
        </g>

        {/* Bottom card: Produit */}
        <g>
          <rect x="160" y="250" width="120" height="46" rx="10" fill="white" fillOpacity="0.08" stroke="#A78BFA" strokeWidth="0.8" strokeOpacity="0.25">
            <animate attributeName="stroke-opacity" values="0.25;0.45;0.25" dur="2.5s" repeatCount="indefinite" />
          </rect>
          <text x="180" y="274" fill="white" fontSize="8.5" fontWeight="600" fontFamily="system-ui">Produit</text>
          <text x="180" y="288" fill="white" fillOpacity="0.3" fontSize="6.5" fontFamily="system-ui">Specs & roadmap</text>
        </g>
      </svg>
    </div>
  );
}

export default function RegisterPage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!firstName.trim()) { setError("Le prenom est requis"); return; }
    if (password.length < 6) { setError("Le mot de passe doit contenir au moins 6 caracteres"); return; }
    setLoading(true);
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const result = await register(email, password, fullName, firstName, lastName, company);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left - Animated diagram */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-indigo-700 to-violet-800 relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <AnimatedDiagramRegister />
        <div className="absolute bottom-12 left-12 z-10">
          <h2 className="font-heading text-3xl font-bold text-white tracking-tight leading-tight mb-2">
            Créez votre espace<br />de travail visuel
          </h2>
          <p className="text-white/40 text-sm max-w-xs leading-relaxed">
            Planifiez, organisez et partagez vos diagrammes d'entreprise.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-gray-900 mb-1">
            CardFlow
          </h1>
          <p className="text-sm text-gray-400 mb-8">
            Créez votre compte
          </p>

          {error && (
            <div
              className="bg-rose-50 border border-rose-200 rounded-lg p-3 mb-5 text-sm text-rose-600"
              data-testid="register-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-semibold text-gray-500">Prenom *</Label>
                <Input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="rounded-lg border-gray-200" data-testid="register-firstname-input" placeholder="Jean" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-semibold text-gray-500">Nom</Label>
                <Input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="rounded-lg border-gray-200" data-testid="register-lastname-input" placeholder="Dupont" />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-gray-500">Entreprise <span className="text-gray-300">(optionnel)</span></Label>
              <Input type="text" value={company} onChange={(e) => setCompany(e.target.value)} className="rounded-lg border-gray-200" data-testid="register-company-input" placeholder="Mon Entreprise SAS" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-gray-500">Email *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-lg border-gray-200" data-testid="register-email-input" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-gray-500">Mot de passe *</Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-lg border-gray-200 pr-10" placeholder="Min. 6 caracteres" data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-register-password" tabIndex={-1}>
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors disabled:opacity-50 shadow-sm mt-1"
              data-testid="register-submit-btn"
            >
              <UserPlus size={16} />
              {loading ? "Inscription..." : "S'inscrire"}
            </button>
          </form>

          <p className="mt-6 text-sm text-center text-gray-400">
            Deja inscrit ?{" "}
            <Link
              to="/login"
              className="text-indigo-600 font-semibold hover:text-indigo-700"
              data-testid="login-link"
            >
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
